import {Injectable} from '@angular/core';
import {Tile} from '../../shared/models/tile';
import {BehaviorSubject, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {TileValueType} from '../../shared/models/tile-value-parser';
import {DialogBoxButtonColor, DialogBoxOption} from '../../shared/models/dialog-box';
import {ObjectModel} from '../../shared/models/object.model';
import {DeviceId} from '../../shared/models/device';
import {EscapeKeyService} from './escape-key.service';
import {UtilsService} from './utils.service';

export enum ShadeToolType {
  Empty = '',
  SHADE_TILE_MAKER = 'SHADE_TILE_MAKER',
  SHADE_DIALOG_BOX = 'SHADE_DIALOG_BOX',
  SHADE_USER_SHARING_DIALOG_BOX = 'SHADE_USER_SHARING_DIALOG_BOX'
}

/**
 * These are all the possible properties that can be used across all types of
 * shade tools. If two tools use the same property, it's only incidental and
 * we can just reuse the property name since only one shade tool can appear at
 * a time.
 */
export interface ShadeServiceProps {
  tile?: Tile;
  useCase?: string;
  rawValue?: TileValueType;
  header?: string;
  message?: string;
  messageHtml?: string;
  dialogOptions?: DialogBoxOption[];
  enableInput?: boolean;
  password?: boolean;
  closeOnButtonClick?: boolean;
}

export interface UserSharingDialogProps {
  object?: ObjectModel
}

@Injectable({
  providedIn: 'root',
})
export class ShadeToolsService {
  /**
   * We use simple increments to notify service consumers of change. We don't
   * use changes to currentShadeToolType as a way to notify since it's possible
   * for two consecutive changes of the same type--and if a change in type were
   * how we notify, then subscribers wouldn't know there is something new to
   * render.
   */
  private incrementValue: number = 0;
  incrementer: BehaviorSubject<number>;

  currentShadeToolType: ShadeToolType = ShadeToolType.Empty;
  props: ShadeServiceProps = {};
  userSharingDialogProps: UserSharingDialogProps = {};
  object: ObjectModel;
  private allDialogsClosed$ = new Subject<boolean>();
  private userSharingDialogClosed$ = new Subject<boolean>();
  private unregisterEsc = () => {};

  get onAllDialogsClosed$() {
    return this.allDialogsClosed$.asObservable();
  }

  get onUserSharingDialogClosed$() {
    return this.userSharingDialogClosed$.asObservable();
  }

  constructor(private escapeKeyService: EscapeKeyService) {
    this.incrementer = new BehaviorSubject<number>(this.incrementValue);
  }


  render() {
    this.incrementer.next(this.incrementValue++);
  }


  /**
   * A simple dialog box with an "OK" button
   *
   * @param header
   * @param message
   * @param closeOnButtonClick
   */
  simpleOkDialog(header: string, message: string, closeOnButtonClick: boolean = true) {
    this.startDialog(header, message, [{
      text: 'OK',
      callback: () => {
      },
      color: DialogBoxButtonColor.Default,
    }], false, false, false, closeOnButtonClick);
  }


  confirmDialog(header: string, message: string, confirmText: string = 'Confirm', cancelText: string = 'Cancel'): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let settled = false;

      const settle = (confirmed: boolean) => {
        if (settled) {
          return;
        }

        settled = true;
        closedSub.unsubscribe();
        resolve(confirmed);
      };

      const closedSub = this.onAllDialogsClosed$.pipe(take(1)).subscribe(() => settle(false));

      this.startDialog(header, message, [
        {
          text: confirmText,
          callback: () => {
            settle(true);
            this.closeAll();
          },
          color: DialogBoxButtonColor.Red,
        },
        {
          text: cancelText,
          callback: () => this.closeAll(),
          color: DialogBoxButtonColor.Alt,
        },
      ], false, false, false, false);
    });
  }


  infoDialog(message: string, header?: string) {
    if (!header) {
      header = 'More Info';
    }
    header = `<i class="far fa-info-circle"></i> ${header}`;
    this.simpleOkDialog(header, message);
  }


  errorDialog(message?: string | unknown) {
    const DEFAULT = 'Something went wrong. Please try again.';
    let resolved: string;

    if (typeof message === 'string') {
      resolved = message || DEFAULT;
    } else if (message != null) {
      // Handles HttpErrorResponse (extract from .error body) or a raw error body object
      const body = (message as any)?.error ?? message;
      resolved = UtilsService.extractErrorMessage(body) || DEFAULT;
    } else {
      resolved = DEFAULT;
    }

    let header = '<span class="red"><i class="far fa-diamond-exclamation"></i> Error</span>';
    this.simpleOkDialog(header, resolved);
  }

  updateAppDialog(
    message: string = 'A new version of the app is available.',
    storeUrl: string = '',
  ) {
    let header = '<span class="red"><i class="far fa-exclamation-triangle"></i> App Update Required</span>';
    this.startDialog(header, message, [{
      text: 'Update',
      callback: () => {
        if (storeUrl) {
          window.open(storeUrl, '_blank');
        }
      },
      color: DialogBoxButtonColor.Default,
    }], false, false, false, true);
  }


  unauthorizedDialog() {
    let header = '<span class="red"><i class="far fa-ban"></i> Unauthorized</span>';
    this.simpleOkDialog(header, 'You are not authorized to perform this action.');
  }


  startDialog(
    header: string,
    message: string,
    options: DialogBoxOption[],
    enableInput = false,
    password: boolean = false,
    htmlMessage: boolean = false,
    closeOnButtonClick: boolean = true,
  ) {
    this.currentShadeToolType = ShadeToolType.SHADE_DIALOG_BOX;
    this.props.header = header;
    this.props.dialogOptions = options;
    this.props.enableInput = enableInput;
    this.props.password = password;
    this.props.closeOnButtonClick = closeOnButtonClick;

    if (htmlMessage) {
      this.props.messageHtml = message;
      this.props.message = '';
    } else {
      this.props.message = message;
      this.props.messageHtml = '';
    }

    this.unregisterEsc();
    this.unregisterEsc = this.escapeKeyService.register(() => this.closeAll());

    this.render();
  }

  openUserSharingDialog(object: ObjectModel) {
    this.userSharingDialogProps.object = object;
    this.currentShadeToolType = ShadeToolType.SHADE_USER_SHARING_DIALOG_BOX;

    this.unregisterEsc();
    this.unregisterEsc = this.escapeKeyService.register(() => this.closeAll());

    this.render();
  }



  closeAll() {
    this._closeDialog(this.currentShadeToolType);
  }

  closeModal(modalType: ShadeToolType) {
    this._closeDialog(modalType);
  }

  private _closeDialog(type: ShadeToolType) {
    // Unregister ESC key handling
    this.unregisterEsc();

    this.currentShadeToolType = ShadeToolType.Empty;
    if (type === ShadeToolType.SHADE_USER_SHARING_DIALOG_BOX) {
      this.userSharingDialogClosed$.next(true);
    }
    if (type === ShadeToolType.SHADE_DIALOG_BOX) {
      this.allDialogsClosed$.next(true);
    }
    this.render();
  }
}
